/**
 * CRM-LIST-VIEWS-V1 (V1-P7) -- the leads-list URL STATE CODEC (C-9).
 *
 * THE URL IS THE CANONICAL VIEW-STATE REPRESENTATION for the canonical
 * leads list (/dashboard/leads): view id, filter tokens, sort, and visible
 * lawful column ids -- versioned (`v=1`), allowlisted, length-bounded.
 * Pure: no React, no fetch, no clock.
 *
 * CONTRACT PROPERTIES (pinned in lead-list-url.test.ts):
 *   - parse -> normalize -> serialize is DETERMINISTIC; equivalent states
 *     produce ONE canonical query string (fixed key order, vocabulary-
 *     ordered tokens, defaults serialize as ABSENT).
 *   - UNKNOWN keys are dropped; unknown/retired tokens inside known keys
 *     are dropped (the server independently ignores + discloses them).
 *   - UNAUTHORIZED column/filter ids are removed during role-aware
 *     normalization (normalizeColumnsForRole; source cleared for
 *     front-desk-tier roles) -- and their data is structurally absent from
 *     the wire regardless (tier shaping precedes rendering).
 *   - Values are LENGTH-BOUNDED; nothing over the bound parses.
 *   - NO PHI: every value is a controlled token or bounded integer. The
 *     free-text search box deliberately NEVER enters the URL (names typed
 *     into search must not land in bookmarks/history/logs -- the C-9 PHI
 *     ruling; search stays ephemeral client state, disclosed on-page).
 *
 * `clinicId` stays OUTSIDE this codec: it is the EXISTING page param with
 * its own server-side scope gate (unchanged by this package).
 */

import {
  EMPTY_LEAD_LIST_FILTERS,
  LEAD_LIST_DEFAULT_SORT,
  LEAD_LIST_MAX_AGE_HOURS,
  LEAD_LIST_SLA_CLASSES,
  LEAD_LIST_SORTS,
  LEAD_LIST_SOURCES,
  LEAD_LIST_STAGES,
  normalizeColumnsForRole,
  viewByIdForRole,
  type LeadListFilterState,
  type LeadListRole,
  type LeadListSortKey,
  type LeadListStage,
  type LeadListSource,
  type LeadListViewState,
  leadListTierForRole,
} from './lead-list-contract';
import type { SlaBreachClass } from '@/dashboard/sla-leakage/sla-leakage-view';

/** URL state version token (C-9 versioned state). Present on every
 *  serialized list URL; an unknown version parses as the role default
 *  (honest reset, never a guess). */
export const LEAD_LIST_URL_VERSION = '1';

/** Single-value length bound; comma-list bound is per-token. */
const VALUE_MAX_LENGTH = 200;
const TOKEN_SHAPE = /^[a-z0-9_]{1,40}$/;

function bounded(v: string | null): string | null {
  if (v === null) return null;
  const s = v.trim();
  if (s.length === 0 || s.length > VALUE_MAX_LENGTH) return null;
  return s;
}

function splitTokens(v: string | null): string[] {
  const s = bounded(v);
  if (s === null) return [];
  const out: string[] = [];
  for (const part of s.split(',')) {
    const t = part.trim();
    if (t.length === 0 || t.length > 64) continue;
    if (!out.includes(t)) out.push(t);
  }
  return out;
}

/**
 * Parse + role-normalize the list state from URL search params. Unknown
 * keys/tokens drop silently here (the SERVER additionally discloses
 * ignored tokens it receives); unauthorized state is removed per role.
 */
export function parseLeadListUrl(
  params: URLSearchParams,
  role: LeadListRole,
): LeadListViewState {
  const stageTokens = splitTokens(params.get('stage'));
  const stages = LEAD_LIST_STAGES.map((s) => s.token).filter((t) =>
    stageTokens.includes(t),
  ) as readonly LeadListStage[];

  const slaTokens = splitTokens(params.get('sla'));
  const slaClasses = LEAD_LIST_SLA_CLASSES.map((c) => c.token).filter((t) =>
    slaTokens.includes(t),
  ) as readonly SlaBreachClass[];

  // Role-aware: source filtering is unlawful for front-desk-tier roles
  // (FQ-1) -- normalized OUT so the UI never carries it (the server would
  // ignore + disclose it anyway).
  const sourceTokens =
    leadListTierForRole(role) === 'front_desk' ? [] : splitTokens(params.get('source'));
  const sources = LEAD_LIST_SOURCES.map((s) => s.token).filter((t) =>
    sourceTokens.includes(t),
  ) as readonly LeadListSource[];

  let assignee: string | null = null;
  const assigneeRaw = bounded(params.get('assignee'));
  if (assigneeRaw !== null) {
    if (assigneeRaw === 'me' || assigneeRaw === 'unassigned') {
      assignee = assigneeRaw;
    } else if (/^[0-9]{1,12}$/.test(assigneeRaw)) {
      assignee = assigneeRaw;
    }
  }

  let olderThanHours: number | null = null;
  const ageRaw = bounded(params.get('olderThanHours'));
  if (ageRaw !== null && /^[0-9]{1,5}$/.test(ageRaw)) {
    const n = Number(ageRaw);
    if (Number.isInteger(n) && n >= 1 && n <= LEAD_LIST_MAX_AGE_HOURS) {
      olderThanHours = n;
    }
  }

  const flag = (key: string): boolean => {
    const v = bounded(params.get(key));
    return v === '1' || v?.toLowerCase() === 'true';
  };

  let sort: LeadListSortKey = LEAD_LIST_DEFAULT_SORT;
  const sortRaw = bounded(params.get('sort'));
  if (sortRaw !== null) {
    const found = LEAD_LIST_SORTS.find((s) => s.key === sortRaw);
    if (found !== undefined) sort = found.key;
  }

  let view: string | null = null;
  const viewRaw = bounded(params.get('view'));
  if (viewRaw !== null && TOKEN_SHAPE.test(viewRaw)) {
    // Only view ids lawful for THIS role survive (role-aware normalization).
    view = viewByIdForRole(role, viewRaw) !== null ? viewRaw : null;
  }

  const columns = normalizeColumnsForRole(role, splitTokens(params.get('cols')));

  return {
    view,
    filters: {
      stages,
      slaClasses,
      assignee,
      sources,
      olderThanHours,
      noContact: flag('noContact'),
      callbackOverdue: flag('callbackOverdue'),
      linkPending: flag('linkPending'),
    },
    sort,
    columns,
  };
}

/**
 * Serialize the state to its ONE canonical query-param list (deterministic
 * key order; defaults absent; vocabulary-ordered tokens). `clinicId` is
 * appended by the caller (the existing page param, kept first for URL
 * stability with old bookmarks).
 */
export function serializeLeadListUrl(
  state: LeadListViewState,
  role: LeadListRole,
): URLSearchParams {
  const out = new URLSearchParams();
  const f = state.filters;
  const isEmpty =
    state.view === null &&
    state.columns === null &&
    state.sort === LEAD_LIST_DEFAULT_SORT &&
    filtersEqual(f, EMPTY_LEAD_LIST_FILTERS);
  if (isEmpty) return out; // the bare canonical URL (no version key needed)

  out.set('v', LEAD_LIST_URL_VERSION);
  if (state.view !== null && TOKEN_SHAPE.test(state.view)) out.set('view', state.view);
  const stages = LEAD_LIST_STAGES.map((s) => s.token).filter((t) => f.stages.includes(t));
  if (stages.length > 0) out.set('stage', stages.join(','));
  const sla = LEAD_LIST_SLA_CLASSES.map((c) => c.token).filter((t) =>
    f.slaClasses.includes(t),
  );
  if (sla.length > 0) out.set('sla', sla.join(','));
  if (f.assignee !== null) out.set('assignee', f.assignee);
  const sources = LEAD_LIST_SOURCES.map((s) => s.token).filter((t) =>
    f.sources.includes(t),
  );
  if (sources.length > 0) out.set('source', sources.join(','));
  if (f.olderThanHours !== null) out.set('olderThanHours', String(f.olderThanHours));
  if (f.noContact) out.set('noContact', '1');
  if (f.callbackOverdue) out.set('callbackOverdue', '1');
  if (f.linkPending) out.set('linkPending', '1');
  if (state.sort !== LEAD_LIST_DEFAULT_SORT) out.set('sort', state.sort);
  const columns = normalizeColumnsForRole(role, state.columns);
  if (columns !== null) out.set('cols', columns.join(','));
  return out;
}

export function filtersEqual(a: LeadListFilterState, b: LeadListFilterState): boolean {
  return (
    a.assignee === b.assignee &&
    a.olderThanHours === b.olderThanHours &&
    a.noContact === b.noContact &&
    a.callbackOverdue === b.callbackOverdue &&
    a.linkPending === b.linkPending &&
    a.stages.length === b.stages.length &&
    a.stages.every((s) => b.stages.includes(s)) &&
    a.slaClasses.length === b.slaClasses.length &&
    a.slaClasses.every((s) => b.slaClasses.includes(s)) &&
    a.sources.length === b.sources.length &&
    a.sources.every((s) => b.sources.includes(s))
  );
}

/** The API query-param map for a state (the server contract's tokens --
 *  same names, same canonical order; the fetch layer appends clinicId +
 *  limit). The page ALWAYS sets `view` (the active view id or 'custom') so
 *  every list fetch takes the versioned list_v1 response shape -- legacy
 *  callers that send no list param keep the byte-identical legacy shape. */
export function apiParamsForState(
  state: LeadListViewState,
  role: LeadListRole,
  activeViewId: string | null,
): Record<string, string> {
  const params = serializeLeadListUrl(state, role);
  // The API accepts the same tokens; 'v' and 'cols' are URL-only concerns.
  params.delete('v');
  params.delete('cols');
  const out: Record<string, string> = {};
  params.forEach((value, key) => {
    out[key] = value;
  });
  out.view = activeViewId ?? 'custom';
  return out;
}
